/**
 * OnboardingChecklist · iter v29 r274
 *
 * Banner di onboarding "actionable" mostrato in dashboard al primo accesso.
 * Diverso da OnboardingMagico (tour informativo) — qui ci sono 4 azioni
 * concrete che l'agente deve fare per attivare davvero Pronto.
 *
 * Comportamento:
 *   • Mostrato in cima alla dashboard se `step < total` AND `!dismissed`
 *   • Lo stato è derivato server-side dal DB (no flag locali) → sempre accurato
 *   • Click su un item non-completato → naviga alla pagina giusta
 *   • Tre stati visivi: pending (cerchio vuoto), done (check verde), all-done
 *   • Pulsante "Non mostrare più" → POST /api/onboarding/dismiss
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, ArrowRight, X as XIcon, Sparkle } from "@phosphor-icons/react";

export default function OnboardingChecklist() {
  const [state, setState] = useState(null);
  const [hidden, setHidden] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api
      .get("/onboarding/state")
      .then((r) => {
        if (!alive) return;
        setState(r.data);
      })
      .catch(() => { /* silenzioso, non rompere la dashboard */ });
    return () => { alive = false; };
  }, []);

  if (hidden || !state || state.dismissed || state.complete) {
    return null;
  }

  const handleDismiss = async () => {
    setHidden(true);
    try { await api.post("/onboarding/dismiss"); } catch { /* silenzioso */ }
  };

  const pct = Math.round((state.step / state.total) * 100);

  return (
    <div
      className="bg-gradient-to-br from-[#F1F4ED] to-[#E5EAD9] border border-[#D5DAC9] rounded-xl p-5 sm:p-6 relative mb-6"
      data-testid="onboarding-checklist"
    >
      <button
        onClick={handleDismiss}
        aria-label="Chiudi onboarding"
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/50 text-[#6B6A65] transition-colors"
        data-testid="onboarding-dismiss"
      >
        <XIcon size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center shrink-0">
          <Sparkle size={18} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0 pr-8">
          <h3 className="font-editorial text-[19px] sm:text-[22px] text-[#1C1C1A] leading-tight">
            Attiva Pronto in 4 passi
          </h3>
          <p className="text-[12.5px] text-[#5A6A4D] mt-1 leading-snug">
            {state.step} di {state.total} fatti — manca poco per essere operativo.
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-white/60 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-[#7C8B6E] transition-all duration-500 rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Items */}
      <div className="space-y-2">
        {state.items.map((it) => (
          <button
            key={it.key}
            onClick={() => !it.done && navigate(it.cta_path)}
            disabled={it.done}
            className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition-colors ${
              it.done
                ? "bg-white/40 cursor-default"
                : "bg-white hover:bg-white/80 border border-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] cursor-pointer"
            }`}
            data-testid={`onboarding-item-${it.key}`}
          >
            {/* Status circle */}
            <span
              className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center transition-colors ${
                it.done
                  ? "bg-[#7C8B6E] text-white"
                  : "bg-[#F1F4ED] border-2 border-[#D5DAC9]"
              }`}
            >
              {it.done && <Check size={12} weight="bold" />}
            </span>

            {/* Label + hint */}
            <div className="flex-1 min-w-0">
              <div
                className={`text-[13.5px] font-medium leading-tight ${
                  it.done ? "text-[#5A6A4D] line-through" : "text-[#1C1C1A]"
                }`}
              >
                {it.label}
              </div>
              {!it.done && (
                <div className="text-[11px] text-[#6B6A65] mt-0.5 leading-snug">
                  {it.hint}
                </div>
              )}
            </div>

            {/* CTA arrow */}
            {!it.done && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#7C8B6E]">
                {it.cta_label}
                <ArrowRight size={12} weight="bold" />
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
